'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Heart, Star, MapPin, Phone, Calendar } from 'lucide-react';

const favouritePriests = [
  {
    id: '1',
    name: 'Pandit Ramesh Kumar',
    title: 'Vedic Ritual Specialist',
    tradition: 'Shaiva Agama',
    experience: '22 years',
    languages: ['Hindi', 'Sanskrit', 'Kannada'],
    location: 'Bengaluru',
    rating: 4.9,
    reviews: 312,
    phone: '+91 98765 43210',
    specialties: ['Ganesh Puja', 'Griha Pravesh', 'Satyanarayan Puja'],
    emoji: '🙏',
  },
  {
    id: '2',
    name: 'Pandit Arun Joshi',
    title: 'Jyotish & Puja Expert',
    tradition: 'Vaishnava',
    experience: '18 years',
    languages: ['Hindi', 'Sanskrit', 'Telugu'],
    location: 'Hyderabad',
    rating: 4.8,
    reviews: 245,
    phone: '+91 87654 32109',
    specialties: ['Navgraha Shanti', 'Lakshmi Puja', 'Rudrabhishek'],
    emoji: '🌺',
  },
  {
    id: '3',
    name: 'Pandit Suresh Sharma',
    title: 'Tantric & Vedic Scholar',
    tradition: 'Shakta',
    experience: '30 years',
    languages: ['Hindi', 'Sanskrit', 'Bengali'],
    location: 'Kolkata',
    rating: 5.0,
    reviews: 487,
    phone: '+91 76543 21098',
    specialties: ['Durga Puja', 'Kali Puja', 'Navratri Havan'],
    emoji: '🔱',
  },
];

export default function FavouritePriests() {
  const [removed, setRemoved] = useState<string[]>([]);

  const visible = favouritePriests.filter(p => !removed.includes(p.id));

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="font-display text-xl font-bold text-maroon">Favourite Priests</h2>
        <p className="text-sm text-maroon/50 mt-0.5">{visible.length} saved priests</p>
      </div>

      {visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gold/15 p-10 text-center">
          <div className="text-4xl mb-3">🙏</div>
          <p className="text-maroon font-semibold">No favourite priests yet</p>
          <p className="text-sm text-maroon/50 mt-1 mb-4">Browse priests and save your favourites</p>
          <Link href="/priests" className="text-sm font-semibold text-white bg-saffron px-5 py-2 rounded-full hover:bg-saffron/90 transition-colors">
            Browse Priests
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((priest) => (
            <div key={priest.id} className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
              <div className="px-5 py-4 flex items-start gap-4">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-xl saffron-gradient flex items-center justify-center text-2xl flex-shrink-0 shadow-sm">
                  {priest.emoji}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-maroon">{priest.name}</h3>
                      <p className="text-xs text-maroon/50">{priest.title}</p>
                    </div>
                    <button
                      onClick={() => setRemoved(prev => [...prev, priest.id])}
                      className="text-rose-400 hover:text-rose-600 transition-colors flex-shrink-0"
                      title="Remove from favourites"
                    >
                      <Heart size={18} className="fill-rose-400" />
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Star size={12} className="text-gold fill-gold" />
                      <span className="text-xs font-bold text-maroon">{priest.rating}</span>
                      <span className="text-xs text-maroon/40">({priest.reviews})</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-maroon/50">
                      <MapPin size={11} /> {priest.location}
                    </div>
                    <span className="text-xs text-maroon/50">{priest.experience}</span>
                  </div>

                  <div className="flex flex-wrap gap-1 mt-2">
                    {priest.languages.map(lang => (
                      <span key={lang} className="text-[10px] bg-cream text-maroon/60 px-2 py-0.5 rounded-full">{lang}</span>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1 mt-2">
                    {priest.specialties.map(s => (
                      <span key={s} className="text-[10px] bg-saffron/10 text-saffron px-2 py-0.5 rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gold/10 flex flex-wrap gap-2">
                <Link
                  href={`/priests/${priest.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-maroon/60 border border-gold/20 px-3 py-1.5 rounded-full hover:bg-cream transition-colors"
                >
                  View Profile
                </Link>
                <Link
                  href={`/booking-flow?priest=${priest.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-white bg-saffron px-3 py-1.5 rounded-full hover:bg-saffron/90 transition-colors"
                >
                  <Calendar size={12} /> Book Now
                </Link>
                <a
                  href={`tel:${priest.phone}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-maroon/60 border border-gold/20 px-3 py-1.5 rounded-full hover:bg-cream transition-colors"
                >
                  <Phone size={12} /> Call
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
